// ABOUTME: Manages agent execution display with reasoning log and flip card final output.
// ABOUTME: Shows thought process during execution, then flippable results card when complete.

import { useState } from 'react';
import { motion } from 'framer-motion';
import { Wrench, FlipHorizontal2, Pin, PinOff, BookOpen, ChevronUp, ChevronDown } from 'lucide-react';
import type { ContentBlock, ToolResultBlock, ToolUseBlock, TextBlock, SmartAction } from '@/types/sdk';
import { RationaleLog, type ProgressStage } from './RationaleLog';
import { ContentBlockRenderer } from './ContentBlockRenderer';
import { TextResponseDisplay } from './TextResponseDisplay';
import { PureQueryResultBlock } from './PureQueryResultBlock';
import { parsePureQueryResult, type PureQueryResult } from '@/utils/exportUtils';

// Single learning entry — shows name + truncated detail, click to expand full detail
function LearningEntry({ learning }: { learning: { id: string; name: string; truncatedDetail: string; detail: string } }) {
  const [expanded, setExpanded] = useState(false);
  const hasMore = learning.detail.length > learning.truncatedDetail.length;

  return (
    <div className="text-xs text-surface-500">
      <span className="font-medium text-surface-700">{learning.name}</span>
      {learning.truncatedDetail && (
        <>
          {' — '}
          {expanded ? learning.detail : learning.truncatedDetail}
          {hasMore && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="ml-1 text-primary-500 hover:text-primary-600 cursor-pointer"
            >
              {expanded ? 'less' : 'more'}
            </button>
          )}
        </>
      )}
    </div>
  );
}

// Inline component: collapsed summary of write_learning tool calls
function WriteLearningsSummary({ blocks }: { blocks: ToolUseBlock[] }) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (blocks.length === 0) return null;

  // Extract learning info from each block's input
  const TRUNCATE_LENGTH = 80;
  const learnings = blocks.map((block) => {
    const input = block.input as Record<string, unknown>;
    const data = input.data as Record<string, unknown> | undefined;
    const entryType = (input.learning_type as string) || 'note';
    const name = (data?.display_name as string) || (data?.name as string) || (data?.title as string) || 'Unknown';
    const detail = (data?.description as string) || (data?.content as string) || (data?.notes as string) || '';
    const truncatedDetail = detail.length > TRUNCATE_LENGTH
      ? detail.slice(0, TRUNCATE_LENGTH) + '...'
      : detail;
    return { id: block.id, entryType, name, detail, truncatedDetail };
  });

  const count = learnings.length;
  const summaryText = count === 1
    ? 'Learned 1 new definition'
    : `Learned ${count} new definitions`;

  return (
    <div className="rounded-sm border border-surface-200 bg-surface-50 overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-surface-100 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-primary-500" />
          <span className="text-sm font-medium text-surface-700">{summaryText}</span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-surface-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-surface-400" />
        )}
      </button>
      {isExpanded && (
        <div className="px-4 pb-3 space-y-2">
          {learnings.map((learning) => (
            <LearningEntry key={learning.id} learning={learning} />
          ))}
        </div>
      )}
    </div>
  );
}

interface AgentExecutionViewProps {
  content: ContentBlock[];
  toolResults: Map<string, ToolResultBlock>;
  agentDurationMs?: number;
  isComplete: boolean;
  currentPhase?: string;
  onPin?: () => void;
  onUnpin?: () => void;
  isPinned?: boolean;
  showPinButton?: boolean;
  showSmartActions?: boolean;
  onSmartAction?: (action: SmartAction) => void;
  onSendFollowUp?: (message: string) => void;
  /** If a clarification in this message was already resolved, this is the user's selected label. */
  clarificationResolvedLabel?: string;
}

export function AgentExecutionView({
  content,
  toolResults,
  agentDurationMs,
  isComplete,
  currentPhase,
  onPin,
  onUnpin,
  isPinned,
  showPinButton,
  showSmartActions: _showSmartActions,
  onSmartAction: _onSmartAction,
  onSendFollowUp: _onSendFollowUp,
  clarificationResolvedLabel,
}: AgentExecutionViewProps) {
  const [isFlipped, setIsFlipped] = useState(false);

  const toolBlocks: ContentBlock[] = [];
  const pureQueryBlocks: ToolUseBlock[] = []; // execute_pure_query tool calls for primary rendering
  const clarificationBlocks: ToolUseBlock[] = []; // resolve_ambiguity tool calls for inline rendering
  const writeLearningBlocks: ToolUseBlock[] = []; // write_learning tool calls for collapsed summary
  const plainTextBlocks: Array<{ block: TextBlock; index: number }> = []; // Regular text (assistant's conversation context)
  let lastToolBlockIndex = -1;

  // Build a merged toolResults map that includes both passed-in results (from streaming)
  // and results embedded in content blocks (from history loading)
  const mergedToolResults = new Map(toolResults);
  content.forEach((block) => {
    if (block.type === 'tool_result') {
      const resultBlock = block as ToolResultBlock;
      mergedToolResults.set(resultBlock.tool_use_id, resultBlock);
    }
  });

  content.forEach((block, index) => {
    if (block.type === 'tool_use') {
      lastToolBlockIndex = Math.max(lastToolBlockIndex, index);
      const toolUseBlock = block as ToolUseBlock;
      // Check if this is the resolve_ambiguity tool (exact match or MCP prefixed)
      const mcpMatch = toolUseBlock.name.match(/^mcp__[^_]+__(.+)$/);
      const normalizedName = mcpMatch ? mcpMatch[1] : toolUseBlock.name;
      const isClarificationTool = normalizedName === 'resolve_ambiguity';
      const isWriteLearningTool = normalizedName === 'write_learning';
      const isPureQueryTool = normalizedName === 'execute_pure_query';

      // Route resolve_ambiguity to clarificationBlocks for inline rendering
      if (isClarificationTool) {
        clarificationBlocks.push(toolUseBlock);
      } else if (isWriteLearningTool) {
        writeLearningBlocks.push(toolUseBlock);
      } else if (isPureQueryTool) {
        pureQueryBlocks.push(toolUseBlock);
        // Also add to toolBlocks so they appear in technical details
        toolBlocks.push(block);
      } else {
        // Add all other tool calls to toolBlocks for display on back of card
        // Skip SQL/code tool calls only when their primary input is empty
        const hasSqlField = 'sql' in toolUseBlock.input;
        const hasCodeField = 'code' in toolUseBlock.input;
        const isEmptySqlOrCode =
          (hasSqlField && !(toolUseBlock.input.sql as string)?.trim()) ||
          (hasCodeField && !(toolUseBlock.input.code as string)?.trim());

        if (!isEmptySqlOrCode) {
          toolBlocks.push(block);
        }
      }
    } else if (block.type === 'tool_result') {
      lastToolBlockIndex = Math.max(lastToolBlockIndex, index);
    } else if (block.type === 'text') {
      // Plain text — assistant's conversation context, shown as fallback
      const textBlock = block as TextBlock;
      if (textBlock.text && textBlock.text.trim().length > 0) {
        plainTextBlocks.push({ block: textBlock, index });
      }
    }
  });

  // Extract structured data only for the last execute_pure_query call in agent order.
  const finalPureQueryBlock = pureQueryBlocks.at(-1);
  const finalPureQueryToolResult = finalPureQueryBlock ? mergedToolResults.get(finalPureQueryBlock.id) : undefined;
  const finalPureQueryParsedResult = finalPureQueryToolResult ? extractPureQueryResult(finalPureQueryToolResult) : null;
  const finalPureQueryResult = finalPureQueryBlock && finalPureQueryParsedResult
    ? { block: finalPureQueryBlock, result: finalPureQueryParsedResult }
    : undefined;
  const hasPureQueryResults = finalPureQueryResult !== undefined;

  // Check if we have plain text content (not empty) - these are assistant's internal notes
  const hasPlainText = plainTextBlocks.length > 0;
  const finalPlainTextBlock = plainTextBlocks
    .filter(({ index }) => index > lastToolBlockIndex)
    .at(-1)?.block;
  const finalAssistantText = finalPlainTextBlock
    ? getFinalAssistantText(finalPlainTextBlock.text, {
        hasPureQueryResults,
        isLegacySingleTextBlock: plainTextBlocks.length === 1 && finalPlainTextBlock !== undefined,
      })
    : undefined;

  const hasTechnicalDetails = toolBlocks.length > 0;
  const hasClarifications = clarificationBlocks.length > 0;
  const hasWriteLearnings = writeLearningBlocks.length > 0;

  // Show final assistant text below any structured result, but avoid duplicating clarification flows.
  const shouldShowPlainTextBlocks = Boolean(finalAssistantText) && hasPlainText && !hasClarifications;
  const hasPendingPureQuery = pureQueryBlocks.some((block) => !mergedToolResults.has(block.id));
  const progressStage: ProgressStage = hasPendingPureQuery
    ? 'executing'
    : finalAssistantText
      ? 'formatting'
      : hasPureQueryResults
        ? 'reviewing'
        : content.length > 0 || currentPhase
          ? 'composing'
          : 'reading';

  // During execution: show the reasoning log (with loading state if no entries yet)
  if (!isComplete) {
    return (
      <RationaleLog
        entries={[]}
        isLoading={true}
        currentPhase={currentPhase}
        isActive={true}
        progressStage={progressStage}
      />
    );
  }

  // After completion: only show card if we have displayable content
  if (!hasPlainText && !hasClarifications && !hasWriteLearnings && !hasPureQueryResults) {
    return null;
  }

  // After completion with content: show flippable card
  return (
    <div className="relative" style={{ perspective: '1000px' }}>
      <motion.div
        className="relative w-full"
        initial={false}
        animate={{ rotateY: isFlipped ? 180 : 0 }}
        transition={{ duration: 0.5, ease: 'easeInOut' }}
        style={{ transformStyle: 'preserve-3d' }}
      >
        {/* Front side - Final Output */}
        <div
          className={`w-full card rounded-sm overflow-hidden ${isFlipped ? 'pointer-events-none' : ''}`}
          style={{ backfaceVisibility: 'hidden' }}
        >
          {/* Banner - always shown when there's pinnable content or technical details */}
          {(hasTechnicalDetails || showPinButton) && (
            <div className="sticky top-0 z-20 bg-surface-0 border-b border-surface-200">
              <div className="flex items-center justify-between px-5 py-2.5">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-surface-500">
                    Response
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {hasTechnicalDetails && (
                    <button
                      onClick={() => setIsFlipped(true)}
                      className="flex items-center gap-1.5 px-2 py-1.5 rounded-sm hover:bg-surface-100 text-surface-400 hover:text-surface-700 transition-all cursor-pointer"
                      title="View tool calls"
                    >
                      <Wrench className="w-3.5 h-3.5" />
                      <span className="text-xs">Tool Calls</span>
                    </button>
                  )}
                  {showPinButton && (isPinned ? (
                    <button
                      onClick={onUnpin}
                      className="flex items-center gap-1.5 px-2 py-1.5 rounded-sm bg-primary-50 text-primary-600 hover:bg-primary-100 transition-all cursor-pointer"
                      title="Unpin from dashboard"
                    >
                      <PinOff className="w-3.5 h-3.5" />
                      <span className="text-xs">Unpin</span>
                    </button>
                  ) : onPin && (
                    <button
                      onClick={onPin}
                      className="flex items-center gap-1.5 px-2 py-1.5 rounded-sm hover:bg-surface-100 text-surface-400 hover:text-surface-700 transition-all cursor-pointer"
                      title="Pin to dashboard"
                    >
                      <Pin className="w-3.5 h-3.5" />
                      <span className="text-xs">Pin</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Final text response - only show if no structured insight */}
          <div className="p-5 space-y-4">
            {/* Clarification cards — show trace when resolved, interactive card when not */}
            {hasClarifications && clarificationResolvedLabel ? (
              /* Resolved: show the streaming-style trace with a "User selected" final step */
              <RationaleLog
                entries={[
                  {
                    id: 'user-selection',
                    toolName: 'user_feedback',
                    message: clarificationResolvedLabel,
                    timestamp: Date.now(),
                  },
                ]}
                isActive={true}
                isComplete={true}
              />
            ) : (
              /* Clarification cards removed for Goldman POC */
              null
            )}

            {/* Write learning summary — collapsed by default, between clarifications and text */}
            {hasWriteLearnings && <WriteLearningsSummary blocks={writeLearningBlocks} />}

            {/* Pure query result — show only the final query the agent sent. */}
            {finalPureQueryResult && (
              <PureQueryResultBlock
                key={`pure-query-${finalPureQueryResult.block.id}`}
                result={finalPureQueryResult.result}
                agentDurationMs={agentDurationMs}
              />
            )}

            {/* Final assistant text response — rendered as markdown below structured results. */}
            {shouldShowPlainTextBlocks && finalAssistantText && (
              <TextResponseDisplay content={finalAssistantText} inline />
            )}

          </div>
        </div>

        {/* Back side - Technical Details */}
        <div
          className={`absolute top-0 left-0 w-full h-full card rounded-sm overflow-hidden flex flex-col ${!isFlipped ? 'pointer-events-none' : ''}`}
          style={{
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
          }}
        >
          {/* Fixed header with flip button */}
          <div className="flex-shrink-0 bg-surface-0 border-b border-surface-200">
            <div className="flex items-center justify-between px-5 py-2.5">
              <div className="flex items-center gap-2">
                <Wrench className="w-3.5 h-3.5 text-primary-600" />
                <span className="text-xs font-medium text-surface-600">
                  Technical Details
                </span>
                <span className="text-[10px] text-surface-400">
                  ({toolBlocks.length} {toolBlocks.length === 1 ? 'step' : 'steps'})
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsFlipped(false)}
                  className="flex items-center gap-1.5 px-2 py-1.5 rounded-sm hover:bg-surface-100 text-surface-400 hover:text-surface-700 transition-all cursor-pointer"
                  title="Flip to response"
                >
                  <FlipHorizontal2 className="w-3.5 h-3.5" />
                  <span className="text-xs">Flip</span>
                </button>
                {showPinButton && (isPinned ? (
                  <button
                    onClick={onUnpin}
                    className="flex items-center gap-1.5 px-2 py-1.5 rounded-sm bg-primary-50 text-primary-600 hover:bg-primary-100 transition-colors cursor-pointer"
                    title="Unpin from dashboard"
                  >
                    <PinOff className="w-3.5 h-3.5" />
                    <span className="text-xs">Unpin</span>
                  </button>
                ) : onPin && (
                  <button
                    onClick={onPin}
                    className="flex items-center gap-1.5 px-2 py-1.5 rounded-sm hover:bg-surface-100 text-surface-400 hover:text-surface-700 transition-colors cursor-pointer"
                    title="Pin to dashboard"
                  >
                    <Pin className="w-3.5 h-3.5" />
                    <span className="text-xs">Pin</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Scrollable content area */}
          <div className="flex-1 overflow-y-auto p-5 space-y-4">

            {/* Rationale log for context */}
            <RationaleLog
              entries={[]}
              isActive={false}
            />

            {/* Full tool call details */}
            <div className="space-y-4 pt-4 border-t border-surface-200">
              {toolBlocks.map((block, index) => (
                <ContentBlockRenderer
                  key={`tool-${index}`}
                  block={block}
                  toolResults={mergedToolResults}
                />
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

/**
 * Extract a PureQueryResult from a tool_result content block.
 * The backend returns two content blocks: text preview + structured JSON.
 * During streaming, content is the raw SDK array. After loading from DB,
 * content may be a string or already-parsed data.
 */
function extractPureQueryResult(toolResult: ToolResultBlock): PureQueryResult | null {
  if (toolResult.is_error) return null;

  const content = toolResult.content;

  // Handle array of content blocks (streaming path)
  if (Array.isArray(content)) {
    for (const item of content) {
      if (typeof item === 'object' && item !== null) {
        const block = item as Record<string, unknown>;
        if (block.type === 'text' && typeof block.text === 'string') {
          const text = (block.text as string).trim();
          if (text.startsWith('{')) {
            try {
              const parsed = JSON.parse(text);
              const result = parsePureQueryResult(parsed);
              if (result) return result;
            } catch {
              // Not valid JSON, continue
            }
          }
        }
        // Check if the item itself is a PureQueryResult (loaded from DB data field)
        const result = parsePureQueryResult(block);
        if (result) return result;
      }
    }
  }

  // Handle string content (may be JSON)
  if (typeof content === 'string') {
    const trimmed = content.trim();
    if (trimmed.startsWith('{')) {
      try {
        const parsed = JSON.parse(trimmed);
        return parsePureQueryResult(parsed);
      } catch {
        // Not valid JSON
      }
    }
  }

  return null;
}

function getFinalAssistantText(
  text: string,
  {
    hasPureQueryResults,
    isLegacySingleTextBlock,
  }: { hasPureQueryResults: boolean; isLegacySingleTextBlock: boolean }
): string {
  const trimmed = text.trim();
  if (!hasPureQueryResults || !isLegacySingleTextBlock) {
    return trimmed;
  }

  const paragraphs = trimmed
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

  return paragraphs.at(-1) ?? trimmed;
}
